import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PractitionerCard } from "@/components/PractitionerCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SEO } from "@/components/SEO";

export default function Featured() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loc, setLoc] = useState("all");
  const [interests, setInterests] = useState([]);
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/practitioners/locations").then((r) => {
      const flat = r.data.flatMap((l) => l.cities.map((c) => ({ city: c, country: l.country })));
      setLocations(flat);
    });
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    if (user) {
      if (user.city_name && user.country) setLoc(`${user.city_name}::${user.country}`);
      if (user.interests?.length) setInterests(user.interests);
    }
  }, [user]);

  useEffect(() => {
    const params = {};
    if (loc && loc !== "all") {
      const [c, co] = loc.split("::");
      params.city = c;
      params.country = co;
    }
    if (interests.length) params.interests = interests.join(",");
    api.get("/featured", { params }).then((r) => setItems(r.data));
  }, [loc, interests]);

  const toggle = (name) => setInterests((cur) => cur.includes(name) ? cur.filter((i) => i !== name) : [...cur, name]);

  return (
    <div className="silk-hero min-h-[70vh] px-5 sm:px-8 py-16">
      <SEO title="Featured Leaders — O.R.E.C" description="Featured, vetted practitioners near you — matched to your location and interests." />
      <div className="max-w-7xl mx-auto" data-testid="featured-page">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Featured</p>
        <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-3">Featured Leaders</h1>
        <p className="text-sm text-muted-foreground mb-8 max-w-xl leading-relaxed">
          Hand-vetted practitioners shining near you — matched to your location and interests. Travelling? Switch the city and see who's lighting up your destination.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-10">
          <Select value={loc} onValueChange={setLoc}>
            <SelectTrigger className="w-56" data-testid="featured-city-select">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locations.map((l) => (
                <SelectItem key={`${l.city}::${l.country}`} value={`${l.city}::${l.country}`}>{l.city}, {l.country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-2" data-testid="featured-interest-chips">
            {categories.map((c) => (
              <button key={c.id} onClick={() => toggle(c.name)} data-testid={`featured-chip-${c.slug || c.id}`}
                className={`rounded-full px-4 py-1.5 text-xs border transition-colors ${interests.includes(c.name) ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {items === null ? (
          <p className="text-muted-foreground py-16 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="featured-empty">
            <Star size={30} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-6">No Featured Leaders here just yet — the spotlight is waiting.</p>
            <Button variant="outline" className="rounded-full px-7" onClick={() => navigate("/browse")} data-testid="featured-browse-all-btn">
              Browse all practitioners
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-grid">
            {items.map((p) => (
              <div key={p.id} className="relative">
                {p.match && (
                  <span className="absolute -top-2.5 left-5 z-10 text-[10px] uppercase tracking-[0.15em] bg-primary text-primary-foreground rounded-full px-3 py-1" data-testid={`featured-match-${p.id}`}>
                    Matches your interests ✦
                  </span>
                )}
                <PractitionerCard p={p} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
